import {
  CopyButton,
  DataTable,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import type { GEOMETRIC_TRANSFORMATION_CALCULATOR_STRINGS } from "@/components/tools/geometric-transformation-calculator";
import { TextMatrix } from "@/components/tools/linear-algebra/text-matrix";
import {
  type Guide,
  type Stage,
  TransformPlot,
} from "@/components/tools/linear-algebra/transform-plot";
import { t } from "@/lib/i18n/interpolate";
import { formatQuad } from "@/lib/tool-kit/linear-algebra/quad-number";
import {
  applyMatrix,
  type Coord,
  composeSteps,
  imageArea,
  linearDeterminant,
  type Point,
  polygonArea,
  type Step,
  type StepMatrix,
  stepMatrix,
} from "@/lib/tool-kit/linear-algebra/transform";
import {
  formatStyled,
  intR,
  mulR,
  type NumberStyle,
  signR,
  toNumberR,
} from "@/lib/tool-kit/math/rational";
import { CHART_COLORS } from "@/lib/tool-kit/palette";

type Strings = typeof GEOMETRIC_TRANSFORMATION_CALCULATOR_STRINGS;

export interface TransformCtx {
  strings: Strings;
  style: NumberStyle;
  mark: string;
  fmt: (value: number, options?: Intl.NumberFormatOptions) => string;
}

const LEADING_HYPHEN = /^-/;
const LETTERS = "ABCDEFGHIJKL";

function decimal(ctx: TransformCtx, value: number): string {
  const rounded = Math.abs(value) < 1e-10 ? 0 : value;
  return ctx
    .fmt(rounded, { maximumFractionDigits: 6 })
    .replace(LEADING_HYPHEN, "−");
}

function coordText(ctx: TransformCtx, c: Coord): string {
  if (c.exact === null) {
    return decimal(ctx, c.value);
  }
  const text = formatQuad(c.exact, ctx.style, ctx.mark);
  return c.exact.b.num === BigInt(0)
    ? text
    : `${text} ≈ ${decimal(ctx, c.value)}`;
}

function matrixCells(ctx: TransformCtx, m: StepMatrix): string[][] {
  return m.numeric.map((row, i) =>
    row.map((value, j) => {
      const exact = m.exact?.[i][j];
      return exact
        ? formatQuad(exact, ctx.style, ctx.mark)
        : decimal(ctx, value);
    })
  );
}

function guideOf(step: Step): Guide | null {
  if (step.type === "reflect") {
    const { line } = step;
    switch (line.kind) {
      case "x-axis":
        return { kind: "line", m: 0, b: 0 };
      case "y-axis":
        return { kind: "vertical", c: 0 };
      case "origin":
        return { kind: "point", at: { x: 0, y: 0 } };
      case "y=x":
        return { kind: "line", m: 1, b: 0 };
      case "y=-x":
        return { kind: "line", m: -1, b: 0 };
      case "vertical":
        return { kind: "vertical", c: toNumberR(line.c) };
      default:
        return { kind: "line", m: toNumberR(line.m), b: toNumberR(line.b) };
    }
  }
  if (step.type === "rotate" || step.type === "dilate") {
    return {
      kind: "point",
      at: { x: toNumberR(step.center.x), y: toNumberR(step.center.y) },
    };
  }
  return null;
}

/** The product of |k| over the dilations: how much every distance is multiplied. */
function distanceScale(steps: Step[]) {
  return steps.reduce(
    (total, step) =>
      step.type === "dilate"
        ? mulR(total, signR(step.k) < 0 ? mulR(step.k, intR(-1)) : step.k)
        : total,
    intR(1)
  );
}

export function TransformResults({
  points,
  steps,
  stepTexts,
  ctx,
}: {
  points: Point[];
  steps: Step[];
  stepTexts: string[];
  ctx: TransformCtx;
}) {
  const { strings } = ctx;
  const composite = composeSteps(steps);
  const images = points.map((p) => applyMatrix(composite, p));
  const det = linearDeterminant(composite);
  const scale = distanceScale(steps);
  const before = points.length >= 3 ? polygonArea(points) : null;
  const after = imageArea(images);
  const names = points.map((_, i) => LETTERS[i]);
  const original = points.map((p) => ({
    x: toNumberR(p.x),
    y: toNumberR(p.y),
  }));
  const stages: Stage[] = [
    { points: original, color: CHART_COLORS[0], labels: names },
  ];
  for (let k = 1; k < steps.length; k += 1) {
    const partial = composeSteps(steps.slice(0, k));
    stages.push({
      points: points.map((p) => {
        const out = applyMatrix(partial, p);
        return { x: out.x.value, y: out.y.value };
      }),
      color: CHART_COLORS[4],
      faint: true,
    });
  }
  stages.push({
    points: images.map((p) => ({ x: p.x.value, y: p.y.value })),
    color: CHART_COLORS[3],
    labels: names.map((n) => `${n}′`),
  });
  const guides = steps.map(guideOf).filter((g): g is Guide => g !== null);
  const rows = points.map((p, i) => [
    `${names[i]} → ${names[i]}′`,
    t(strings.pointText, {
      x: formatStyled(p.x, ctx.style, ctx.mark),
      y: formatStyled(p.y, ctx.style, ctx.mark),
    }),
    t(strings.pointText, {
      x: coordText(ctx, images[i].x),
      y: coordText(ctx, images[i].y),
    }),
  ]);
  const detText = coordText(ctx, det);
  const orientation =
    det.value < 0 ? strings.orientationReversed : strings.orientationPreserved;
  const copy = rows.map((r) => `${r[0]}: ${r[1]} → ${r[2]}`).join("\n");
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.distanceScaleLabel}
          value={formatStyled(scale, ctx.style, ctx.mark)}
        />
        <StatCard label={strings.determinantLabel} value={detText} />
        <StatCard label={strings.orientationLabel} value={orientation} />
        {before && after ? (
          <StatCard
            label={strings.areaLabel}
            value={t(strings.areaChange, {
              before: formatStyled(before, ctx.style, ctx.mark),
              after: coordText(ctx, after),
            })}
          />
        ) : null}
      </StatGrid>
      <h3 className={WIDGET_H3}>{strings.imagesHeading}</h3>
      <DataTable
        head={[strings.colPoint, strings.colOriginal, strings.colImage]}
        rows={rows}
      />
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyImages}
        small
        text={copy}
      />
      <h3 className={WIDGET_H3}>{strings.matrixHeading}</h3>
      <p className="text-muted-foreground text-sm">{strings.matrixIntro}</p>
      <div className="flex flex-wrap items-center gap-3">
        <span className="font-medium text-foreground text-sm">
          {strings.compositeLabel}
        </span>
        <TextMatrix
          cells={matrixCells(ctx, composite)}
          label={strings.compositeLabel}
        />
      </div>
      {composite.exact === null ? (
        <p className="text-muted-foreground text-xs">{strings.decimalNote}</p>
      ) : null}
      {steps.length > 1 ? (
        <details className="rounded-lg border border-border bg-card p-4">
          <summary className="cursor-pointer font-medium text-foreground text-sm">
            {strings.stepMatrices}
          </summary>
          <ol className="mt-3 space-y-3">
            {steps.map((step, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: steps are an ordered list
              <li className="flex flex-wrap items-center gap-3" key={i}>
                <span className="text-muted-foreground text-sm">
                  {stepTexts[i]}
                </span>
                <TextMatrix
                  cells={matrixCells(ctx, stepMatrix(step))}
                  label={t(strings.stepMatrixLabel, { n: ctx.fmt(i + 1) })}
                />
              </li>
            ))}
          </ol>
        </details>
      ) : null}
      <h3 className={WIDGET_H3}>{strings.plotHeading}</h3>
      <TransformPlot
        downloadLabel={strings.downloadPng}
        guides={guides}
        stages={stages}
        title={strings.plotTitle}
      />
      <p className="text-muted-foreground text-xs">{strings.plotKey}</p>
    </>
  );
}
